import type { PodBrand } from '@/types/pod'

/**
 * The ReZabond Training pod — branding layer (browser side).
 *
 * WHAT THIS IS, AND WHAT IT IS NOT. This is a REVISION AID for students who have
 * already sat one of Wayne Hock's training courses. It is not a sales surface
 * and it captures no leads. It exists because Wayne wanted to give delegates a
 * printed booklet and was uneasy about the material walking out of the room and
 * being copied; a question-at-a-time assistant hands over an answer without ever
 * handing over the document. Students keep access after the course, so they can
 * ask a question standing on a real site months later — that longevity is the
 * point, not a side effect.
 *
 * SEPARATE POD FROM `rezabond`, DELIBERATELY. The rezabond pod is Gary's sales
 * demo, grounded in rezabond.co.uk, with lead capture ON and a safety fence that
 * refuses inspection verdicts to prospects. This pod is grounded in Wayne's
 * course decks, has capture OFF, and speaks to students rather than buyers. Same
 * company, entirely different job — merging them would put sales pricing in
 * front of students and course content in front of prospects.
 *
 * SOURCE OF TRUTH. Contact details below are ReZabond's PUBLIC routes as
 * published on rezabond.co.uk — the same values the rezabond pod carries, which
 * is correct: it is the same business, and a student who needs a human needs the
 * same switchboard.
 *
 * PALETTE — PROVISIONAL, INHERITED. Identical to the rezabond pod's palette and
 * carrying the same caveat: ReZabond publish no brand hex values and their site
 * is a Wix template, so these are a considered choice rather than a sampled
 * brand match. Kept identical on purpose — two assistants from one company
 * should not look like two companies. Sample their logo and replace in BOTH pods
 * together, never one alone.
 */
export const rezabondTrainingPod: PodBrand = {
  id: 'rezabond-training',
  brandName: 'ReZabond Training',
  productName: 'ReZabond Training',
  assistantName: 'Reva',
  tagline: 'Revision support for ReZabond play safety and surfacing courses',
  roleLabel: 'course revision assistant',
  welcomeIntro:
    "Ask me anything from your ReZabond course and I'll answer from Wayne's own training material — inspections, standards, equipment checks, surfacing, or the revision questions. If it isn't in the course notes I'll tell you so rather than guess, and your question goes to Wayne.",
  starters: [
    'What are the three inspection frequencies?',
    'What is the maximum wear on a swing chain?',
    'What are the risk scoring bands?',
    'What are the resin mixing ratios?',
  ],
  inputPrompt: 'Ask Reva a revision question',
  footerNote:
    "Reva answers only from Wayne's course material. Anything it can't answer goes to Wayne.",
  theme: {
    // PROVISIONAL — see header note. Matches pods/rezabond/config.ts exactly.
    '--color-brand-primary': '22 78 55', // deep green #164E37
    '--color-brand-primary-hover': '15 56 39', // darker green #0F3827
    '--color-brand-accent': '234 128 35', // safety orange #EA8023
    '--color-surface-base': '247 249 247', // off-white #F7F9F7
    '--color-surface-raised': '255 255 255',
    '--color-surface-subtle': '237 242 238', // pale green-grey #EDF2EE
    '--color-text-primary': '26 38 32', // near-black green #1A2620
    '--color-text-muted': '100 116 106', // muted green-grey #64746A
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '221 230 224', // soft green-grey #DDE6E0
  },
  contact: {
    phone: '029 2168 1288',
    email: 'info@rezabond.co.uk',
  },
}
